<script setup lang="ts">
  import type { PropType } from 'vue'
  import { computed } from 'vue'

  import { EyeOffIcon, Lock01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { useLevelUp2026 } from '@common/core-layer/composables/useLevelUp2026'
  import { createElementId } from '@common/core-layer/utils'

  import { ChannelVisibility } from '../types'
  import {
    getChannelEmojiByType,
    getChannelIcon,
    hasChannelIcon,
    isChannelIconEmoji,
    isHiddenPrivateChannel,
  } from '../utils/channel.helper'

  import type { IChannel } from '../types'

  const props = defineProps({
    channel: {
      type: Object as PropType<Partial<IChannel>>,
      required: true,
    },
    size: {
      type: Number,
      default: 24,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    showLockIcon: {
      type: Boolean,
      default: true,
    },
    textColor: {
      type: String,
      default: 'text-secondary',
    },
    /** Owning list passes the viewer's admin state so each row doesn't spin up group page context. */
    isGroupAdmin: {
      type: Boolean,
      default: false,
    },
  })

  const displayIcon = computed(() => {
    if (props.channel.icon && isChannelIconEmoji(props.channel.icon)) {
      return props.channel.icon
    }

    if (props.channel.icon && hasChannelIcon(props.channel.icon)) {
      return null
    }

    return getChannelEmojiByType(props.channel)
  })

  const svgIcon = computed(() => {
    if (props.channel.icon && hasChannelIcon(props.channel.icon)) {
      return getChannelIcon(props.channel.icon)
    }
    return null
  })

  const { isLevelUp2026Enabled } = useLevelUp2026()

  /**
   * Private channels get one overlay: eye-off for admins when the channel is hidden from discovery, otherwise a lock.
   * Callers that render their own private indicator suppress the overlay via showLockIcon.
   */
  const overlay = computed(() => {
    if (!props.showLockIcon || props.channel.visibility !== ChannelVisibility.PRIVATE) return null

    const isHiddenForAdmin = isLevelUp2026Enabled.value && props.isGroupAdmin && isHiddenPrivateChannel(props.channel)
    if (isHiddenForAdmin) return { key: 'hidden', icon: EyeOffIcon }

    return { key: 'lock', icon: Lock01Icon }
  })

  const overlayIconSize = computed(() => Math.max(4, props.size - 8))

  const badgeSupSize = computed(() => overlayIconSize.value + 4)

  /** Bottom-right offset (mirrors previous CPBadge top-right offsets). */
  const lockOffsetStyle = computed(() => (props.size === 18 ? { right: '-2px', bottom: '-4px' } : { right: '-4.5px', bottom: '-5px' }))

  const iconTextClass = computed(() => (props.textColor === 'inherit' ? 'text-inherit' : props.textColor))
</script>

<template>
  <div
    :id="createElementId('channel-badge', channel._id || 'unknown', 'badge')"
    class="channel-icon-badge relative inline-flex items-center justify-start"
    :style="{ '--channel-icon-badge-size': `${badgeSupSize}px` }"
  >
    <div
      :id="createElementId('channel-icon', channel._id || 'unknown', 'container')"
      class="relative inline-flex items-center w-full h-full justify-start"
    >
      <span v-if="displayIcon" :class="[iconTextClass, 'leading-tight']" :style="{ 'font-size': `${size - 4}px` }">
        {{ displayIcon }}
      </span>

      <component :is="svgIcon" v-else-if="svgIcon" :class="[iconTextClass]" :style="{ width: `${size - 4}px`, height: `${size - 4}px` }" />

      <span
        v-if="overlay"
        :id="createElementId('channel-badge', channel._id || 'unknown', overlay.key)"
        class="channel-icon-lock"
        :style="lockOffsetStyle"
        aria-hidden="true"
      >
        <component :is="overlay.icon" class="text-600" :style="{ width: `${overlayIconSize}px`, height: `${overlayIconSize}px` }" />
      </span>
    </div>
  </div>
</template>

<style scoped>
  .channel-icon-lock {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    height: var(--channel-icon-badge-size);
    width: var(--channel-icon-badge-size);
    border-radius: 50%;
    background-color: var(--cp-brand-background);
    padding: 0;
    pointer-events: none;
  }
</style>
